import React, { useMemo } from 'react';
import { Badge, Box, Button, Flex, IconButton, Text, Tooltip, useToast } from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { motion } from 'framer-motion';
import { BookmarkSimple, Sparkle, WarningCircle } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import {
  IContaCilLancamento,
  useContaCilUpdateLancamento,
} from '../../../../../../hooks/useContaCilLancamentos';
import { useContaCilGoldenCurar } from '../../../../../../hooks/useContaCilSchoolAi';
import { useAuth } from '../../../../../../providers/auth';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import {
  PocSelectGroup,
  PocSelectOption,
  PocSelectTooltip,
  POC_COMPONENTS_C,
  POC_COMPONENTS_D,
  STATUS_ACCENT,
  STATUS_BADGE,
  corConfianca,
  fmtData,
  fmtDoc,
  fmtValor,
  pocSelectStyles,
} from '../shared';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';

interface Props {
  lanc: IContaCilLancamento;
  index: number;
  pocOptions: PocSelectOption[];
  pocGroups: PocSelectGroup[];
  onSaved: () => void;
  onOpen: (lanc: IContaCilLancamento) => void;
  empresaId?: number;
}

export function LancamentoCard({
  lanc,
  index,
  pocOptions,
  pocGroups,
  onSaved,
  onOpen,
  empresaId,
}: Props) {
  const toast = useToast();
  const { isTechnical } = useAuth();
  const { mutateAsync, isPending } = useContaCilUpdateLancamento();
  const curarGolden = useContaCilGoldenCurar(empresaId ?? 0);
  // Sprint 3: botao "+ Golden" so' aparece pra tecnico (admin/dev) em lancamentos
  // totalmente conciliados — golden set tem que ter gabarito (POC nos 2 lados).
  const podeCurarGolden =
    isTechnical() &&
    empresaId != null &&
    lanc.status === 'conciliado' &&
    !!lanc.debito_poc &&
    !!lanc.credito_poc;

  const handleCurarGolden = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await curarGolden.mutateAsync({ lancamento_id: lanc.id });
      toast({
        status: 'success',
        title: 'Adicionado ao golden',
        description: 'Vira gabarito da próxima prova de resistência.',
        duration: 3000,
      });
    } catch {
      toast({ status: 'error', title: 'Falha ao curar golden' });
    }
  };

  const isEntrada = lanc.dc === 'C';
  const contraField: 'debito_poc' | 'credito_poc' = isEntrada ? 'credito_poc' : 'debito_poc';
  const contraPocCod = isEntrada ? lanc.credito_poc : lanc.debito_poc;
  // Perna-banco: se a conta-banco não tem POC no plano, fica vazia -> pendente.
  const bankPocCod = isEntrada ? lanc.debito_poc : lanc.credito_poc;
  const faltaContaBanco = lanc.status !== 'conciliado' && !bankPocCod;
  const contraValue = useMemo(
    () => pocOptions.find(o => o.value === contraPocCod) ?? null,
    [pocOptions, contraPocCod],
  );

  const badge = STATUS_BADGE[lanc.status] ?? { cor: 'gray', label: lanc.status };
  const accent = STATUS_ACCENT[lanc.status] ?? '#94A3B8';
  const corValor = isEntrada ? '#10B981' : '#EF4444';
  const pctConf = lanc.confianca != null ? Math.round(lanc.confianca * 100) : null;
  // IA ja analisou (dt_ia_tentativa) mas o lancamento segue pendente e sem
  // contrapartida -> ela nao conseguiu identificar (ex: Pix a PF). Sinaliza.
  const iaNaoIdentificou =
    !!lanc.dt_ia_tentativa && lanc.status === 'pendente' && contraPocCod == null;
  // Extrato de origem com divergência não discriminada pendente: conciliação
  // bloqueada (o PATCH recusa 409) até resolver o quiz na tela de Extratos.
  const extratoBloqueado = !!lanc.extrato_bloqueado;
  // Top sugestão da IA pra preencher inline enquanto não há contrapartida.
  const topSug = !contraPocCod ? lanc.ia_alternativas?.[0] : undefined;

  // Motivo ESPECIFICO de a IA nao ter conciliado este lancamento. Prioriza o
  // aviso textual gravado pelo writer da IA (auto_aviso); senao deriva dos
  // campos do proprio lancamento (sugestao/confianca/contraparte). Fallback
  // generico so quando nao ha nada especifico — substitui o tooltip fixo.
  const motivoIaNaoIdentificou = useMemo(() => {
    if (lanc.auto_aviso) return lanc.auto_aviso;
    const sug = lanc.ia_alternativas?.[0];
    if (sug?.cod) {
      const pct = sug.score != null ? ` (confiança ${Math.round(sug.score * 100)}%)` : '';
      return `A IA sugeriu ${sug.cod}${sug.nm ? ` · ${sug.nm}` : ''}${pct}, mas abaixo do limiar para conciliar sozinha. Confirme ou ajuste manualmente.`;
    }
    if (pctConf != null) {
      return `A IA achou candidatos, mas a confiança máxima foi ${pctConf}% — abaixo do limiar para conciliar sozinha. Selecione a conta manualmente.`;
    }
    if (!lanc.contraparte) {
      return 'A IA não identificou a contraparte deste lançamento (comum em Pix/TED a pessoas físicas). Selecione a conta manualmente — a memória aprende e passa a conciliar parecidos sozinha.';
    }
    return 'A IA analisou e não encontrou uma contrapartida compatível com segurança. Selecione a conta manualmente — a memória aprende e passa a conciliar parecidos sozinha.';
  }, [lanc.auto_aviso, lanc.ia_alternativas, lanc.contraparte, pctConf]);

  const salvar = async (valor: string | null) => {
    try {
      await mutateAsync({ id: lanc.id, [contraField]: valor });
      onSaved();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao vincular contrapartida',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.04 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <GlassCard
        intensity="medium"
        hoverable
        tint={accent}
        p={0}
        overflow="hidden"
        cursor="pointer"
        role="button"
        tabIndex={0}
        aria-label={`Lançamento ${fmtData(lanc.data)} ${isEntrada ? 'entrada' : 'saída'} ${fmtValor(
          lanc.valor,
        )}, ${badge.label}`}
        onClick={() => onOpen(lanc)}
        onKeyDown={ev => {
          if (ev.defaultPrevented || ev.target !== ev.currentTarget) return;
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            onOpen(lanc);
          }
        }}
      >
        <Flex>
          <Box w="4px" bg={accent} flexShrink={0} alignSelf="stretch" />
          <Flex direction="column" gap={2.5} p={3.5} flex={1} minW={0}>
            {/* data · tipo + status */}
            <Flex align="center" justify="space-between" gap={2}>
              <Text fontSize="12px" color="#64748B" fontWeight="600" isTruncated>
                {fmtData(lanc.data)}
                {lanc.tipo_operacao && (
                  <Text as="span" color="#94A3B8">
                    {' '}
                    · {lanc.tipo_operacao.toUpperCase()}
                  </Text>
                )}
              </Text>
              <Flex align="center" gap={1.5} flexShrink={0}>
                {podeCurarGolden && (
                  <Tooltip
                    label="Adicionar ao golden set (gabarito da prova de resistência)"
                    hasArrow
                    openDelay={400}
                  >
                    <IconButton
                      aria-label="Adicionar ao golden"
                      icon={<BookmarkSimple size={13} weight="bold" />}
                      size="xs"
                      variant="ghost"
                      color="#06B6D4"
                      _hover={{ bg: 'rgba(6,182,212,0.10)' }}
                      isLoading={curarGolden.isPending}
                      onClick={handleCurarGolden}
                    />
                  </Tooltip>
                )}
                <Badge colorScheme={badge.cor} borderRadius="full" px={2}>
                  {badge.label}
                </Badge>
                {lanc.cross_link && (
                  <Tooltip
                    label="Transferência entre contas da própria empresa — não é receita nem despesa"
                    hasArrow
                    openDelay={300}
                  >
                    <Badge
                      colorScheme="purple"
                      borderRadius="full"
                      px={2}
                      fontSize="9px"
                      textTransform="none"
                    >
                      transf. interna
                    </Badge>
                  </Tooltip>
                )}
              </Flex>
            </Flex>

            {/* valor */}
            <Text
              fontSize="26px"
              fontWeight="700"
              lineHeight="1.05"
              letterSpacing="-0.02em"
              color={corValor}
            >
              {isEntrada ? '+' : '−'} {fmtValor(lanc.valor)}
            </Text>

            {/* histórico */}
            <Tooltip label={lanc.historico} hasArrow openDelay={300}>
              <Text
                fontSize="13px"
                color="#0F172A"
                fontWeight="500"
                lineHeight="1.35"
                noOfLines={2}
              >
                {lanc.historico}
              </Text>
            </Tooltip>

            {/* contraparte */}
            {lanc.contraparte && (
              <Tooltip
                label={
                  lanc.contraparte_cnpj_cpf
                    ? `${lanc.contraparte} · ${fmtDoc(lanc.contraparte_cnpj_cpf)}`
                    : lanc.contraparte
                }
                hasArrow
                openDelay={300}
              >
                <Text fontSize="12px" color="#1B88F5" fontWeight="600" isTruncated>
                  {lanc.contraparte}
                  {lanc.contraparte_cnpj_cpf ? ` · ${fmtDoc(lanc.contraparte_cnpj_cpf)}` : ''}
                </Text>
              </Tooltip>
            )}

            <Box h="1px" bg="rgba(15, 23, 42, 0.06)" />

            {/* contrapartida (não abre o modal) */}
            <Box onClick={ev => ev.stopPropagation()}>
              <Text
                fontSize="9px"
                fontWeight="700"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb="3px"
              >
                Contrapartida
                <Text as="span" color="#CBD5E1" fontWeight="600">
                  {' · '}banco no {isEntrada ? 'débito' : 'crédito'}
                </Text>
              </Text>
              <PocSelectTooltip label={contraValue?.label}>
                <RSelect<PocSelectOption>
                  size="sm"
                  placeholder="Selecione a contrapartida…"
                  options={pocGroups}
                  value={contraValue}
                  onChange={opt => salvar(opt ? opt.value : null)}
                  isClearable
                  isDisabled={isPending || extratoBloqueado}
                  components={isEntrada ? POC_COMPONENTS_C : POC_COMPONENTS_D}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  {...propsMenuSelect}
                  styles={pocSelectStyles}
                  chakraStyles={{ control: p => ({ ...p, borderRadius: 'lg', minHeight: '40px' }) }}
                />
              </PocSelectTooltip>
              {topSug?.cod && (
                <Flex
                  align="center"
                  gap={2}
                  mt={2}
                  px={2.5}
                  py={1.5}
                  borderRadius="lg"
                  bg="rgba(99, 102, 241, 0.06)"
                  borderWidth="1px"
                  borderColor="rgba(99, 102, 241, 0.18)"
                >
                  <Sparkle size={12} weight="fill" color="#6366F1" />
                  <Text
                    fontSize="11px"
                    color="#475569"
                    fontWeight="600"
                    isTruncated
                    flex={1}
                    minW={0}
                  >
                    Sugestão: {topSug.cod} · {topSug.nm}
                  </Text>
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="purple"
                    borderRadius="md"
                    isLoading={isPending}
                    isDisabled={extratoBloqueado}
                    onClick={() => topSug.cod && salvar(topSug.cod)}
                  >
                    Usar
                  </Button>
                </Flex>
              )}
            </Box>

            {/* rodapé: banco · confiança · IA */}
            <Flex align="center" gap={2} wrap="wrap">
              {lanc.banco_nome && (
                <Text fontSize="11px" color="#94A3B8" isTruncated minW={0} flex="1">
                  {lanc.banco_nome}
                  {lanc.conta ? ` · ${lanc.conta}` : ''}
                </Text>
              )}
              {pctConf != null && (
                <Flex align="center" gap={1} flexShrink={0}>
                  <Box w="6px" h="6px" borderRadius="full" bg={corConfianca(pctConf)} />
                  <Text fontSize="11px" color="#64748B" fontWeight="600">
                    {pctConf}%
                  </Text>
                </Flex>
              )}
              {lanc.fonte_match === 'ia' && (
                <Badge
                  borderRadius="full"
                  px={2}
                  fontSize="8px"
                  color="white"
                  bgGradient="linear(135deg, #6366F1, #EC4899)"
                >
                  IA
                </Badge>
              )}
              {extratoBloqueado && (
                <Tooltip
                  label="O extrato de origem tem divergência não discriminada pelo banco pendente de resolução. Abra o extrato na tela Extratos e resolva o aviso para liberar a conciliação."
                  placement="top"
                  hasArrow
                  fontSize="11px"
                  borderRadius="md"
                  maxW="250px"
                  textAlign="center"
                >
                  <Badge
                    borderRadius="full"
                    px={2}
                    py="2px"
                    fontSize="8px"
                    flexShrink={0}
                    display="inline-flex"
                    alignItems="center"
                    gap="3px"
                    color="#B45309"
                    bg="rgba(245,158,11,0.16)"
                    cursor="help"
                  >
                    <WarningCircle size={9} weight="fill" /> divergência no extrato
                  </Badge>
                </Tooltip>
              )}
              {iaNaoIdentificou && (
                <Tooltip
                  label={motivoIaNaoIdentificou}
                  placement="top"
                  hasArrow
                  fontSize="11px"
                  borderRadius="md"
                  maxW="250px"
                  textAlign="center"
                >
                  <Badge
                    borderRadius="full"
                    px={2}
                    py="2px"
                    fontSize="8px"
                    flexShrink={0}
                    display="inline-flex"
                    alignItems="center"
                    gap="3px"
                    color="#B45309"
                    bg="rgba(245,158,11,0.16)"
                    cursor="help"
                  >
                    <Sparkle size={9} weight="fill" /> IA não identificou
                  </Badge>
                </Tooltip>
              )}
              {faltaContaBanco && (
                <Tooltip
                  label={`A conta-banco ${lanc.banco_nome ?? ''}${
                    lanc.conta ? ` · ${lanc.conta}` : ''
                  } não está vinculada a uma POC no plano — por isso fica pendente. Vincule na tela Bancos e reprocesse o extrato.`}
                  placement="top"
                  hasArrow
                  fontSize="11px"
                  borderRadius="md"
                  maxW="250px"
                  textAlign="center"
                >
                  <Badge
                    borderRadius="full"
                    px={2}
                    py="2px"
                    fontSize="8px"
                    flexShrink={0}
                    display="inline-flex"
                    alignItems="center"
                    gap="3px"
                    color="#B45309"
                    bg="rgba(245,158,11,0.16)"
                    cursor="help"
                  >
                    <WarningCircle size={9} weight="fill" /> sem conta-banco
                  </Badge>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        </Flex>
      </GlassCard>
    </motion.div>
  );
}
